import React from 'react';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

import useComponentState from '../../utils/useComponentState';
import { PRINTER_TASKS, connectPrinterForTask } from '../../utils/printerSettings';

import styles from './PrintOptionsModal.module.scss';

const INITIAL_STATE = {
  printing: false,
  excludedNotes: [],
};

const sanitizeZplText = (txt = '') =>
  String(txt)
    .replace(/[\^~]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatAddress = (order) => {
  const parts = [order.houseAddress, order.landmarks, order.city].filter(Boolean);
  return sanitizeZplText(parts.join(', '));
};

const DeliveryStickerModal = (props) => {
  const { show, handleClose, selectedOrders = [], token } = props;

  const [{ printing, excludedNotes }, setState] = useComponentState(INITIAL_STATE);

  const handleModalClose = () => {
    setState(INITIAL_STATE);
    handleClose();
  };

  const toggleNote = (purchaseId) => {
    setState((prev) => ({
      ...prev,
      excludedNotes: prev.excludedNotes.includes(purchaseId)
        ? prev.excludedNotes.filter((id) => id !== purchaseId)
        : [...prev.excludedNotes, purchaseId],
    }));
  };

  const printDeliverySticker = async () => {
    let device = null;
    try {
      device = await connectPrinterForTask(PRINTER_TASKS.deliverySticker.key, token);
      const encoder = new TextEncoder();
      let zpl = '';

      selectedOrders.forEach((order) => {
        const receiver = sanitizeZplText(order.receiverName || 'N/A');
        const contact = sanitizeZplText(order.deliveryMobile || order.creatorMobile || 'N/A');
        const address = formatAddress(order);
        const topField = receiver || 'N/A';
        const fontsize = topField.length <= 20 ? 34 : topField.length <= 30 ? 30 : 22;

        zpl += '^XA\n^CI28\n^PW609\n^LL812\n^LH0,0\n^MD22\n';

        let currentY = 25;
        const spacing = 12;

        zpl += `^FO30,${currentY}^A0N,${fontsize},${fontsize}^FB550,1,0,C^FD${topField}^FS\n`;
        currentY += fontsize + spacing;

        zpl += `^FO30,${currentY}^A0N,23,23^FB550,1,0,C^FD${contact}^FS\n`;
        currentY += 28;

        zpl += `^FO30,${currentY}^A0N,24,24^FB550,3,6,C^FD${address}^FS\n`;
        currentY += 60;

        if (order.deliveryNote && !excludedNotes.includes(order.purchaseId)) {
          const deliveryNote = sanitizeZplText(order.deliveryNote);
          zpl += `^FO30,${currentY}^A0N,20,20^FB550,2,8,C^FDDelivery Note: ${deliveryNote}^FS\n`;
        }

        zpl += '^XZ\n';
      });

      await device.transferOut(1, encoder.encode(zpl));
    } finally {
      if (device) {
        try {
          await device.releaseInterface(0);
        } catch (e) {}
        try {
          await device.close();
        } catch (e) {}
      }
    }
  };

  const handlePrint = async () => {
    setState({ printing: true });
    try {
      await printDeliverySticker();
      toast.success('Delivery sticker printed successfully');
      handleModalClose();
    } catch (error) {
      console.error('Delivery sticker: failed to print', error);
      toast.error('Failed to print delivery sticker');
    } finally {
      setState({ printing: false });
    }
  };

  return (
    <>
      {show && <div className={styles.customBackdrop} onClick={handleModalClose} />}
      <Modal
        show={show}
        onHide={handleModalClose}
        backdrop={false}
        centered
        size='lg'
        className={`${styles.modal} ${styles.stickerModal}`}
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.titleRow}>
            <span>Delivery sticker</span>
            <span className={styles.subtitle}>
              {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className={styles.stickerPreview}>
            {selectedOrders.map((order) => (
              <div key={order.purchaseId} className={styles.stickerPreviewItem}>
                <div className={styles.stickerPreviewHeader}>
                  Purchase ID: {order.purchaseId}
                </div>
                <div className={styles.stickerPreviewRow}>
                  <strong>Receiver:</strong>
                  <span>{order.receiverName}</span>
                </div>
                <div className={styles.stickerPreviewRow}>
                  <strong>Address:</strong>
                  <span>{order.houseAddress}</span>
                </div>
                <div className={styles.stickerPreviewRow}>
                  <strong>City:</strong>
                  <span>{order.city}</span>
                </div>
                <div className={styles.stickerPreviewRow}>
                  <strong>Mobile:</strong>
                  <span>{order.deliveryMobile || order.creatorMobile}</span>
                </div>
                {order.deliveryNote && (
                  <>
                    <div className={styles.stickerPreviewRow}>
                      <strong>Note:</strong>
                      <span>{order.deliveryNote}</span>
                    </div>
                    <Form.Check
                      type='checkbox'
                      id={`sticker-note-${order.purchaseId}`}
                      className={styles.noteCheck}
                      label='Print delivery note on sticker'
                      checked={!excludedNotes.includes(order.purchaseId)}
                      onChange={() => toggleNote(order.purchaseId)}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.footer}>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant='primary'
            onClick={handlePrint}
            disabled={printing || selectedOrders.length === 0}
          >
            {printing ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faPrint} />
                Print
              </span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DeliveryStickerModal;
